/*
 * The one step-up dialog of the application (spec 01 §4). The shell mounts it once; every call that
 * runs through `withStepUp` reaches it, whichever surface made the call.
 *
 * Tier 1 is the password. Tier 2 adds a fresh emailed code and asks for the password first, because
 * the route that mails the code is itself a tier-1 action — unless the password was proved a moment
 * ago for the same action, and the server then asked for the code (`withStepUp`). Escape closes the dialog and
 * abandons the original action — Radix's `Dialog` gives the focus trap and the Escape handling, and
 * `onOpenChange(false)` is the single place where "abandoned" is decided. The body is a form whose
 * submit is *Confirm*, so Enter in the password box or the code confirms, as in every other form.
 */

import { useEffect, useId, useState, type FormEvent } from "react"
import { Button, Dialog, FormAlert } from "../../../ui/index.ts"
import { t } from "../../i18n/index.ts"
import { authApi } from "../api.ts"
import "../auth.css"
import { isCompleteCode } from "../code.ts"
import { CodeInput } from "../components/CodeInput.tsx"
import { PasswordInput } from "../components/PasswordInput.tsx"
import { errorText, type StepUpTier } from "../errors.ts"
import { useCountdown } from "../useCountdown.ts"
import { setStepUpPrompt } from "./stepUp.ts"

/** The cooldown of `auth.stepup.code.request` (security.md §8). */
const CODE_COOLDOWN_SECONDS = 60

interface Pending {
	tier: StepUpTier
	settle: (granted: boolean) => void
}

export function StepUpHost() {
	const [pending, setPending] = useState<Pending | null>(null)
	const [phase, setPhase] = useState<"password" | "code">("password")
	const [password, setPassword] = useState("")
	const [code, setCode] = useState("")
	const [busy, setBusy] = useState(false)
	const [alert, setAlert] = useState("")
	const [status, setStatus] = useState("")
	const [cooldown, startCooldown] = useCountdown()
	const formId = useId()

	useEffect(() => {
		setStepUpPrompt(
			(tier, passwordProved) =>
				new Promise<boolean>((resolve) => {
					setPhase(tier === 2 && passwordProved ? "code" : "password")
					setPassword("")
					setCode("")
					setAlert("")
					setStatus("")
					setPending({ tier, settle: resolve })
				}),
		)
		return () => setStepUpPrompt(null)
	}, [])

	function close(granted: boolean) {
		pending?.settle(granted)
		setPending(null)
		setPassword("")
		setCode("")
	}

	async function submitPassword() {
		if (busy || pending === null) return
		setBusy(true)
		setAlert("")
		try {
			await authApi.stepUpPassword(password)
			if (pending.tier === 1) {
				close(true)
				return
			}
			setPassword("")
			setPhase("code")
		} catch (err) {
			setAlert(errorText(err))
		} finally {
			setBusy(false)
		}
	}

	async function sendCode() {
		if (busy || cooldown > 0) return
		setBusy(true)
		setAlert("")
		try {
			await authApi.requestStepUpCode()
			setStatus(t("auth.stepup.codeSent"))
			startCooldown(CODE_COOLDOWN_SECONDS)
		} catch (err) {
			setAlert(errorText(err))
		} finally {
			setBusy(false)
		}
	}

	async function submitCode() {
		if (busy) return
		setBusy(true)
		setAlert("")
		try {
			await authApi.verifyStepUpCode(code)
			close(true)
		} catch (err) {
			setAlert(errorText(err))
			setCode("")
		} finally {
			setBusy(false)
		}
	}

	const ready = !busy && (phase === "password" ? password !== "" : isCompleteCode(code))

	function submit(event: FormEvent) {
		event.preventDefault()
		if (!ready) return
		void (phase === "password" ? submitPassword() : submitCode())
	}

	return (
		<Dialog
			open={pending !== null}
			onOpenChange={(open) => {
				if (!open) close(false)
			}}
			title={t("auth.stepup.title")}
			description={
				phase === "password" ? t("auth.stepup.passwordIntro") : t("auth.stepup.codeIntro")
			}
			footer={
				<div className="tb-auth__actions">
					<Button variant="neutral" label={t("action.cancel")} onClick={() => close(false)} />
					<Button
						variant="primary"
						type="submit"
						form={formId}
						label={t("auth.stepup.confirm")}
						disabled={!ready}
					/>
				</div>
			}
		>
			<form id={formId} onSubmit={submit}>
				<FormAlert message={alert} />
				{phase === "password" ? (
					<PasswordInput
						label={t("auth.password.current")}
						value={password}
						onChange={setPassword}
						autoComplete="current-password"
						disabled={busy}
						autoFocus
					/>
				) : (
					<>
						<Button
							variant="neutral"
							label={
								cooldown > 0
									? t("auth.code.resendIn", { seconds: cooldown })
									: t("auth.stepup.sendCode")
							}
							disabled={busy || cooldown > 0}
							onClick={() => void sendCode()}
						/>
						<CodeInput value={code} onChange={setCode} disabled={busy} />
						{status === "" ? null : <p className="tb-auth__status">{status}</p>}
					</>
				)}
			</form>
		</Dialog>
	)
}
